import { useTheme } from 'styled-components';
import { WhitePanel } from '../../../../../components/Toolkit/WhitePanel/WhitePanel';
import { Link } from '../../../../../components/Toolkit/Button/Link';
import * as Styled from '../../../../../components/PageWrappers/AdDetails/components/SellerInfoPanelV2/SellerInfoPanelV2.styled';
import { SellerInfoPanelV2Props } from '../../../../../components/PageWrappers/AdDetails/components/SellerInfoPanelV2/SellerInfoPanelV2.typed';
import { AdStats } from '../../../../../components/Ratings/AdStats/AdStats';
import { RatingsDistribution } from '../../../../../components/Ratings/RatingsDistribution/RatingsDistribution';
import { RatingFeedbackChips } from '../../../../../components/Ratings/RatingsFeedbackChips/RatingFeedbackChips';
import { Verifications } from '../../../../../components/PageWrappers/AdDetails/components/Verifications/Verifications';
import { plural } from 'helpers/plural';
import { FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE } from '../../../../../utils';
import { Spacer } from 'helpers/genericStyles';

// Note: SellerInfoPanel has been duplicated here to support the feature flag
// TODO: Once the feature flag is no longer needed we should clean it up and delete the original SellerInfoPanel
function SellerInfoPanelV2({
  profileImageUrl,
  title,
  renderSendMessage,
  renderShowPhone,
  county,
  type,
  sellerUrl,
  isTrader,
  verification,
  avgResponseRate,
  adCountStats,
  className,
  testId,
  userRating,
  membershipDuration,
  ratingsPanelRef,
  sellerInfoPanelRef,
  onClickRatings,
  onClickReviews,
}: SellerInfoPanelV2Props) {
  const { average, reviewCount = 0 } = userRating || {};
  const theme = useTheme();
  const hideButtonOnMobile = FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE;

  const renderButtons = renderSendMessage || renderShowPhone;

  return (
    <WhitePanel className={className} testId={testId}>
      <Styled.SellerInfoPanelWrapper ref={sellerInfoPanelRef} tabIndex={-1}>
        <Styled.ProfileSummary>
          {title && (
            <Styled.MiniUserProfile
              title={title}
              illustration={profileImageUrl}
              county={county}
              type={type}
              isTrader={isTrader}
              verification={verification}
            />
          )}

          {verification && (
            <>
              <Verifications verifications={verification} />
              <Spacer marginBottom={'S8'} />
            </>
          )}
          {onClickRatings && onClickReviews && (
            <Styled.PrivateSellerRatings
              userRating={userRating}
              membershipDuration={membershipDuration}
              onClickRatings={onClickRatings}
              onClickReviews={onClickReviews}
            />
          )}
        </Styled.ProfileSummary>

        <Styled.ActionsAdStats>
          {renderButtons && (
            <Styled.ButtonList>
              {renderSendMessage && (
                <Styled.ButtonListItem hideOnMobile={hideButtonOnMobile}>
                  {renderSendMessage}
                </Styled.ButtonListItem>
              )}
              {renderShowPhone && (
                <Styled.ButtonListItem hideOnMobile={hideButtonOnMobile}>
                  {renderShowPhone}
                </Styled.ButtonListItem>
              )}
            </Styled.ButtonList>
          )}

          <AdStats
            avgResponseRate={avgResponseRate}
            adCountStats={adCountStats}
            sellerUrl={sellerUrl}
            isTrader={isTrader}
          />
        </Styled.ActionsAdStats>
        <Styled.Divider />
        <Styled.RatingsFeedback ref={ratingsPanelRef} tabIndex={-1}>
          <Styled.Ratings fullWidth={!userRating}>
            <Styled.RatingTitle>DoneDeal Rating</Styled.RatingTitle>
            {userRating ? (
              <>
                <Styled.RatingRow>
                  <Styled.StarIcon
                    height={20}
                    width={20}
                    color={theme.colors.ORANGE}
                  />
                  <Styled.RatingText>{average}/5</Styled.RatingText>
                  <Styled.Bullet
                    color={theme.colors.GREY_DARK}
                    width={4}
                    height={4}
                  />
                  <Styled.ReviewCount>
                    {reviewCount} {plural('Review', 'Reviews', reviewCount)}
                  </Styled.ReviewCount>
                </Styled.RatingRow>
                <Styled.RatingsSummary>
                  <RatingsDistribution userRating={userRating} />
                </Styled.RatingsSummary>
              </>
            ) : (
              <Styled.RatingSubTitle data-testid="ratings-subtext">
                This seller does not have a rating yet.
              </Styled.RatingSubTitle>
            )}
          </Styled.Ratings>

          {userRating && <RatingFeedbackChips userRating={userRating} />}
        </Styled.RatingsFeedback>
        <Styled.Divider />
        <Styled.SafeHelpSection>
          <Styled.Title>Find out how we are keeping you safe.</Styled.Title>
          <Link
            href="https://hello.donedeal.ie/hc/en-us/articles/31198796650641-Verified-Profiles"
            ofType="SECONDARY"
            underline
            fontSize="M14"
          >
            Learn More
          </Link>
        </Styled.SafeHelpSection>
      </Styled.SellerInfoPanelWrapper>
    </WhitePanel>
  );
}

export { SellerInfoPanelV2 };
